import { useFinancialMaturity, MaturityCriterion, MATURITY_STATES, MaturityState } from '../hooks/useFinancialMaturity'
import { useLanguage } from '../hooks/useLanguage'
import { 
  Check, 
  AlertCircle, 
  X,
  ChevronRight,
  Shield,
  TrendingUp,
  Eye,
  Zap,
  ArrowLeft
} from 'lucide-react'
import { Link } from 'react-router-dom'

const STATE_ICONS: Record<MaturityState, React.ComponentType<{ className?: string }>> = {
  visibility: Eye,
  stability: Shield,
  control: TrendingUp,
  optimization: Zap,
}

function CriterionStatusIcon({ status }: { status: MaturityCriterion['status'] }) {
  switch (status) {
    case 'complete':
      return (
        <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        </div>
      )
    case 'partial':
      return (
        <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
      )
    case 'missing':
      return (
        <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
          <X className="w-4 h-4 text-slate-400 dark:text-slate-500" />
        </div>
      )
  }
}

export default function MaturityDetail() {
  const { language } = useLanguage()
  const { 
    maturityData, 
    currentStateConfig, 
    isLoading,
    feedbackMessage,
    confirmAction,
    ignoreAction,
  } = useFinancialMaturity()

  if (isLoading) {
    return (
      <div className="space-y-6 pt-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48" />
          <div className="h-32 bg-slate-200 dark:bg-slate-700 rounded-xl" />
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-xl" />
        </div>
      </div>
    )
  }

  const Icon = STATE_ICONS[currentStateConfig.id]
  const name = language === 'it' ? currentStateConfig.nameIt : currentStateConfig.nameEn
  const description = language === 'it' ? currentStateConfig.descriptionIt : currentStateConfig.descriptionEn
  const impact = language === 'it' ? currentStateConfig.impactIt : currentStateConfig.impactEn

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link 
          to="/dashboard"
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
            {language === 'it' ? 'Affidabilità Previsioni' : 'Prediction Reliability'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {language === 'it' ? 'Stato di maturità operativa' : 'Operational maturity status'}
          </p>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedbackMessage && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Check className="w-5 h-5" />
          <span className="font-medium">{feedbackMessage}</span>
        </div>
      )}

      {/* Current State Card */}
      <div className={`
        bg-white dark:bg-slate-800 rounded-xl border p-6
        ${currentStateConfig.borderColor}
      `}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${currentStateConfig.bgColor}`}>
            <Icon className={`w-7 h-7 ${currentStateConfig.color}`} />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {language === 'it' ? 'Stato attuale' : 'Current status'}
              </span>
            </div>
            <h2 className={`text-xl font-bold ${currentStateConfig.color} mb-2`}>
              {name}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-2">
              {description}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {impact}
            </p>
          </div>
        </div>

        {/* State Progress */}
        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
          <div className="grid grid-cols-4 gap-2">
            {(['visibility', 'stability', 'control', 'optimization'] as MaturityState[]).map((state) => {
              const stateConfig = MATURITY_STATES[state]
              const isActive = state === currentStateConfig.id
              const StateIcon = STATE_ICONS[state]
              const currentIndex = ['visibility', 'stability', 'control', 'optimization'].indexOf(currentStateConfig.id)
              const stateIndex = ['visibility', 'stability', 'control', 'optimization'].indexOf(state)
              const isPast = stateIndex < currentIndex
              
              return (
                <div
                  key={state}
                  className={`
                    text-center p-3 rounded-lg transition-all
                    ${isActive 
                      ? `${stateConfig.bgColor} ${stateConfig.borderColor} border-2`
                      : isPast
                        ? 'bg-slate-50 dark:bg-slate-800/50'
                        : 'bg-slate-50 dark:bg-slate-800/30 opacity-50'
                    }
                  `}
                >
                  <StateIcon className={`
                    w-5 h-5 mx-auto mb-1
                    ${isActive || isPast ? stateConfig.color : 'text-slate-400 dark:text-slate-500'}
                  `} />
                  <span className={`
                    text-xs font-medium
                    ${isActive ? stateConfig.color : 'text-slate-500 dark:text-slate-400'}
                  `}>
                    {language === 'it' ? stateConfig.nameIt : stateConfig.nameEn}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Pending Action */}
      {maturityData?.pendingAction && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-amber-800 p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                {language === 'it' 
                  ? maturityData.pendingAction.titleIt 
                  : maturityData.pendingAction.titleEn
                }
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {language === 'it' 
                  ? maturityData.pendingAction.descriptionIt 
                  : maturityData.pendingAction.descriptionEn
                }
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => confirmAction(maturityData.pendingAction!.id)}
              className="flex-1 px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              {language === 'it' ? 'Conferma' : 'Confirm'}
            </button>
            <button
              onClick={() => ignoreAction(maturityData.pendingAction!.id)}
              className="px-4 py-2.5 text-slate-600 dark:text-slate-400 font-medium rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              {language === 'it' ? 'Ignora' : 'Ignore'}
            </button>
          </div>
        </div>
      )}

      {/* Criteria Breakdown */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            {language === 'it' ? 'Dettaglio affidabilità' : 'Reliability breakdown'}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {language === 'it' 
              ? 'Elementi che influenzano le previsioni'
              : 'Elements affecting predictions'
            }
          </p>
        </div>
        
        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {maturityData?.criteria.map((criterion) => (
            <div 
              key={criterion.id}
              className="px-5 py-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            >
              <CriterionStatusIcon status={criterion.status} />
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 dark:text-white">
                  {language === 'it' ? criterion.nameIt : criterion.nameEn}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'it' ? criterion.descriptionIt : criterion.descriptionEn}
                </p>
              </div>
              
              {criterion.status !== 'complete' && criterion.actionIt && (
                <Link
                  to={
                    criterion.category === 'data' ? '/upload' 
                    : criterion.category === 'pattern' ? '/categorize'
                    : '/transactions'
                  }
                  className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                >
                  {language === 'it' ? 'Migliora' : 'Improve'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Footer */}
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
        <p className="text-xs text-slate-500 dark:text-slate-400 text-center leading-relaxed">
          {language === 'it' 
            ? 'Le analisi sono di supporto alle decisioni e non sostituiscono il giudizio professionale. I dati mostrati sono indicativi e basati sulle informazioni disponibili.'
            : 'Analyses support decisions and do not replace professional judgment. The data shown is indicative and based on available information.'
          }
        </p>
      </div>
    </div>
  )
}
